import React, { useState, useEffect } from 'react';
import {
  BookOpen,
  FileText,
  Plus,
  Search,
  Edit,
  Trash2,
  Save,
  X,
  Upload,
  Download,
  Tag,
  Folder
} from 'lucide-react';
import { knowledgeAPI } from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';

const KnowledgeRepositoryPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('articles');

  // Articles State
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [articleSearchQuery, setArticleSearchQuery] = useState('');
  const [articleCategoryFilter, setArticleCategoryFilter] = useState('');

  // Documents State
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [documentSearchQuery, setDocumentSearchQuery] = useState('');
  const [documentCategoryFilter, setDocumentCategoryFilter] = useState('');

  // Categories State
  const [categories, setCategories] = useState([]);

  // Modal State
  const [isArticleModalOpen, setIsArticleModalOpen] = useState(false);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [editingDocument, setEditingDocument] = useState(null);

  // Form State
  const [articleForm, setArticleForm] = useState({
    subject: '',
    content: '',
    keywords: '',
    category: '',
    order: 0,
    isActive: true
  });

  const [documentForm, setDocumentForm] = useState({
    label: '',
    description: '',
    fileUrl: '',
    keywords: '',
    category: '',
    notes: ''
  });

  // Load data on mount
  useEffect(() => {
    loadAllData();
  }, []);

  // Filter articles when search/filter changes
  useEffect(() => {
    filterArticles();
  }, [articles, articleSearchQuery, articleCategoryFilter]);

  // Filter documents when search/filter changes
  useEffect(() => {
    filterDocuments();
  }, [documents, documentSearchQuery, documentCategoryFilter]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadArticles(),
        loadDocuments(),
        loadCategories()
      ]);
    } catch (error) {
      console.error('Failed to load knowledge data:', error);
      toast.error('Failed to load knowledge repository');
    } finally {
      setLoading(false);
    }
  };

  const loadArticles = async () => {
    try {
      const response = await knowledgeAPI.getArticles();
      const articlesData = response.data?.data || response.data || [];
      setArticles(articlesData);
      setFilteredArticles(articlesData);
    } catch (error) {
      console.error('Failed to load articles:', error);
      setArticles([]);
      setFilteredArticles([]);
    }
  };

  const loadDocuments = async () => {
    try {
      const response = await knowledgeAPI.getDocuments();
      const documentsData = response.data?.data || response.data || [];
      setDocuments(documentsData);
      setFilteredDocuments(documentsData);
    } catch (error) {
      console.error('Failed to load documents:', error);
      setDocuments([]);
      setFilteredDocuments([]);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await knowledgeAPI.getCategories();
      const categoriesData = response.data?.data || response.data || [];
      setCategories(categoriesData);
    } catch (error) {
      console.error('Failed to load categories:', error);
      setCategories([]);
    }
  };

  const filterArticles = () => {
    let filtered = articles;

    // Search filter
    if (articleSearchQuery) {
      const query = articleSearchQuery.toLowerCase();
      filtered = filtered.filter(article =>
        article.subject?.toLowerCase().includes(query) ||
        article.content?.toLowerCase().includes(query) ||
        article.keywords?.some(k => k.toLowerCase().includes(query))
      );
    }

    // Category filter
    if (articleCategoryFilter) {
      filtered = filtered.filter(article => article.category === articleCategoryFilter);
    }

    setFilteredArticles(filtered);
  };

  const filterDocuments = () => {
    let filtered = documents;

    // Search filter
    if (documentSearchQuery) {
      const query = documentSearchQuery.toLowerCase();
      filtered = filtered.filter(doc =>
        doc.label?.toLowerCase().includes(query) ||
        doc.description?.toLowerCase().includes(query) ||
        doc.keywords?.some(k => k.toLowerCase().includes(query))
      );
    }

    // Category filter
    if (documentCategoryFilter) {
      filtered = filtered.filter(doc => doc.category === documentCategoryFilter);
    }

    setFilteredDocuments(filtered);
  };

  const openArticleModal = (article = null) => {
    if (article) {
      setEditingArticle(article);
      setArticleForm({
        subject: article.subject || '',
        content: article.content || '',
        keywords: Array.isArray(article.keywords) ? article.keywords.join(', ') : '',
        category: article.category || '',
        order: article.order || 0,
        isActive: article.isActive !== false
      });
    } else {
      setEditingArticle(null);
      setArticleForm({
        subject: '',
        content: '',
        keywords: '',
        category: '',
        order: 0,
        isActive: true
      });
    }
    setIsArticleModalOpen(true);
  };

  const openDocumentModal = (document = null) => {
    if (document) {
      setEditingDocument(document);
      setDocumentForm({
        label: document.label || '',
        description: document.description || '',
        fileUrl: document.fileUrl || '',
        keywords: Array.isArray(document.keywords) ? document.keywords.join(', ') : '',
        category: document.category || '',
        notes: document.notes || ''
      });
    } else {
      setEditingDocument(null);
      setDocumentForm({
        label: '',
        description: '',
        fileUrl: '',
        keywords: '',
        category: '',
        notes: ''
      });
    }
    setIsDocumentModalOpen(true);
  };

  const closeArticleModal = () => {
    setIsArticleModalOpen(false);
    setEditingArticle(null);
    setArticleForm({
      subject: '',
      content: '',
      keywords: '',
      category: '',
      order: 0,
      isActive: true
    });
  };

  const closeDocumentModal = () => {
    setIsDocumentModalOpen(false);
    setEditingDocument(null);
    setDocumentForm({
      label: '',
      description: '',
      fileUrl: '',
      keywords: '',
      category: '',
      notes: ''
    });
  };

  const handleCreateArticle = async () => {
    try {
      setSaving(true);

      // Convert keywords string to array
      const keywordsArray = articleForm.keywords
        .split(',')
        .map(k => k.trim())
        .filter(k => k.length > 0);

      const payload = {
        subject: articleForm.subject,
        content: articleForm.content,
        keywords: keywordsArray,
        category: articleForm.category,
        order: parseInt(articleForm.order) || 0,
        isActive: articleForm.isActive
      };

      await knowledgeAPI.createArticle(payload);
      toast.success('Article created successfully');
      closeArticleModal();
      await loadArticles();
    } catch (error) {
      console.error('Failed to create article:', error);
      toast.error('Failed to create article');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateArticle = async () => {
    try {
      setSaving(true);

      // Convert keywords string to array
      const keywordsArray = articleForm.keywords
        .split(',')
        .map(k => k.trim())
        .filter(k => k.length > 0);

      const payload = {
        subject: articleForm.subject,
        content: articleForm.content,
        keywords: keywordsArray,
        category: articleForm.category,
        order: parseInt(articleForm.order) || 0,
        isActive: articleForm.isActive
      };

      await knowledgeAPI.updateArticle(editingArticle.id, payload);
      toast.success('Article updated successfully');
      closeArticleModal();
      await loadArticles();
    } catch (error) {
      console.error('Failed to update article:', error);
      toast.error('Failed to update article');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteArticle = async (id) => {
    if (!window.confirm('Are you sure you want to delete this article?')) {
      return;
    }

    try {
      await knowledgeAPI.deleteArticle(id);
      toast.success('Article deleted successfully');
      await loadArticles();
    } catch (error) {
      console.error('Failed to delete article:', error);
      toast.error('Failed to delete article');
    }
  };

  const handleToggleArticleActive = async (article) => {
    try {
      const payload = {
        ...article,
        isActive: !article.isActive
      };
      await knowledgeAPI.updateArticle(article.id, payload);
      toast.success(`Article ${payload.isActive ? 'activated' : 'deactivated'}`);
      await loadArticles();
    } catch (error) {
      console.error('Failed to toggle article status:', error);
      toast.error('Failed to update article status');
    }
  };

  const handleCreateDocument = async () => {
    try {
      setSaving(true);

      // Convert keywords string to array
      const keywordsArray = documentForm.keywords
        .split(',')
        .map(k => k.trim())
        .filter(k => k.length > 0);

      const payload = {
        label: documentForm.label,
        description: documentForm.description,
        fileUrl: documentForm.fileUrl,
        keywords: keywordsArray,
        category: documentForm.category,
        notes: documentForm.notes
      };

      await knowledgeAPI.createDocument(payload);
      toast.success('Document uploaded successfully');
      closeDocumentModal();
      await loadDocuments();
    } catch (error) {
      console.error('Failed to create document:', error);
      toast.error('Failed to upload document');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateDocument = async () => {
    try {
      setSaving(true);

      // Convert keywords string to array
      const keywordsArray = documentForm.keywords
        .split(',')
        .map(k => k.trim())
        .filter(k => k.length > 0);

      const payload = {
        label: documentForm.label,
        description: documentForm.description,
        fileUrl: documentForm.fileUrl,
        keywords: keywordsArray,
        category: documentForm.category,
        notes: documentForm.notes
      };

      await knowledgeAPI.updateDocument(editingDocument.id, payload);
      toast.success('Document updated successfully');
      closeDocumentModal();
      await loadDocuments();
    } catch (error) {
      console.error('Failed to update document:', error);
      toast.error('Failed to update document');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDocument = async (id) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      await knowledgeAPI.deleteDocument(id);
      toast.success('Document deleted successfully');
      await loadDocuments();
    } catch (error) {
      console.error('Failed to delete document:', error);
      toast.error('Failed to delete document');
    }
  };

  const getUniqueCategories = () => {
    const articleCategories = articles.map(a => a.category).filter(Boolean);
    const documentCategories = documents.map(d => d.category).filter(Boolean);
    const allCategories = [...new Set([...articleCategories, ...documentCategories, ...categories])];
    return allCategories.sort();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading knowledge repository..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Knowledge Repository</h1>
        <p className="text-gray-600 mt-1">
          Manage knowledge articles and documents for your AI assistant
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'articles', label: 'Knowledge Articles', icon: BookOpen },
            { id: 'documents', label: 'Documents', icon: FileText }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2
                ${activeTab === tab.id
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
              `}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Articles Tab */}
      {activeTab === 'articles' && (
        <div className="bg-white rounded-lg shadow">
          {/* Search and Filter Bar */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={articleSearchQuery}
                  onChange={(e) => setArticleSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex gap-2">
                <select
                  value={articleCategoryFilter}
                  onChange={(e) => setArticleCategoryFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Categories</option>
                  {getUniqueCategories().map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
                <button
                  onClick={() => openArticleModal()}
                  className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Article</span>
                </button>
              </div>
            </div>
          </div>

          {/* Articles List */}
          <div className="p-6">
            {filteredArticles.length === 0 ? (
              <div className="text-center py-12">
                <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No articles found</p>
                <button
                  onClick={() => openArticleModal()}
                  className="mt-4 text-blue-600 hover:text-blue-700"
                >
                  Create your first article
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredArticles.map(article => (
                  <div
                    key={article.id}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {article.subject}
                          </h3>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            article.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {article.isActive ? 'Active' : 'Inactive'}
                          </span>
                          {article.category && (
                            <span className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full">
                              <Folder className="w-3 h-3" />
                              {article.category}
                            </span>
                          )}
                        </div>
                        <p className="text-gray-600 text-sm line-clamp-2 mb-2">
                          {article.content}
                        </p>
                        {article.keywords && article.keywords.length > 0 && (
                          <div className="flex items-center gap-2 flex-wrap">
                            <Tag className="w-3 h-3 text-gray-400" />
                            {article.keywords.map((keyword, idx) => (
                              <span
                                key={idx}
                                className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded"
                              >
                                {keyword}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => handleToggleArticleActive(article)}
                          className={`px-3 py-1 text-sm rounded ${
                            article.isActive
                              ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          }`}
                        >
                          {article.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => openArticleModal(article)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                          title="Edit article"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteArticle(article.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                          title="Delete article"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <div className="bg-white rounded-lg shadow">
          {/* Search and Filter Bar */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search documents..."
                  value={documentSearchQuery}
                  onChange={(e) => setDocumentSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex gap-2">
                <select
                  value={documentCategoryFilter}
                  onChange={(e) => setDocumentCategoryFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Categories</option>
                  {getUniqueCategories().map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
                <button
                  onClick={() => openDocumentModal()}
                  className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  <Upload className="w-4 h-4" />
                  <span>Upload Document</span>
                </button>
              </div>
            </div>
          </div>

          {/* Documents List */}
          <div className="p-6">
            {filteredDocuments.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No documents found</p>
                <button
                  onClick={() => openDocumentModal()}
                  className="mt-4 text-blue-600 hover:text-blue-700"
                >
                  Upload your first document
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredDocuments.map(document => (
                  <div
                    key={document.id}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <FileText className="w-5 h-5 text-blue-600" />
                          <h3 className="text-lg font-semibold text-gray-900">
                            {document.label}
                          </h3>
                          {document.category && (
                            <span className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full">
                              <Folder className="w-3 h-3" />
                              {document.category}
                            </span>
                          )}
                        </div>
                        {document.description && (
                          <p className="text-gray-600 text-sm mb-2">
                            {document.description}
                          </p>
                        )}
                        {document.fileUrl && (
                          <p className="text-gray-500 text-xs mb-2 truncate">
                            <span className="font-medium">File:</span> {document.fileUrl}
                          </p>
                        )}
                        {document.keywords && document.keywords.length > 0 && (
                          <div className="flex items-center gap-2 flex-wrap">
                            <Tag className="w-3 h-3 text-gray-400" />
                            {document.keywords.map((keyword, idx) => (
                              <span
                                key={idx}
                                className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded"
                              >
                                {keyword}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        {document.fileUrl && (
                          <a
                            href={document.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-green-600 hover:bg-green-50 rounded"
                            title="Download document"
                          >
                            <Download className="w-4 h-4" />
                          </a>
                        )}
                        <button
                          onClick={() => openDocumentModal(document)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                          title="Edit document"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteDocument(document.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                          title="Delete document"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Article Modal */}
      {isArticleModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingArticle ? 'Edit Article' : 'Create New Article'}
              </h2>
              <button
                onClick={closeArticleModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject *
                </label>
                <input
                  type="text"
                  value={articleForm.subject}
                  onChange={(e) => setArticleForm({ ...articleForm, subject: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter article subject"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Content *
                </label>
                <textarea
                  value={articleForm.content}
                  onChange={(e) => setArticleForm({ ...articleForm, content: e.target.value })}
                  rows={8}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  placeholder="Enter article content (rich editor coming soon)"
                  required
                />
                <p className="text-sm text-gray-500 mt-1">
                  Rich text editor will be added in a future update
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <div className="flex gap-2">
                  <select
                    value={articleForm.category}
                    onChange={(e) => setArticleForm({ ...articleForm, category: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select or type new category</option>
                    {getUniqueCategories().map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={articleForm.category}
                    onChange={(e) => setArticleForm({ ...articleForm, category: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Or type new category"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Keywords
                </label>
                <input
                  type="text"
                  value={articleForm.keywords}
                  onChange={(e) => setArticleForm({ ...articleForm, keywords: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter keywords separated by commas"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Separate keywords with commas (e.g., support, faq, help)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Display Order
                </label>
                <input
                  type="number"
                  value={articleForm.order}
                  onChange={(e) => setArticleForm({ ...articleForm, order: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                  min="0"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Lower numbers appear first (default: 0)
                </p>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="articleActive"
                  checked={articleForm.isActive}
                  onChange={(e) => setArticleForm({ ...articleForm, isActive: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="articleActive" className="ml-2 text-sm text-gray-700">
                  Active (visible to AI assistant)
                </label>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={closeArticleModal}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={editingArticle ? handleUpdateArticle : handleCreateArticle}
                disabled={saving || !articleForm.subject || !articleForm.content}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                <span>{saving ? 'Saving...' : editingArticle ? 'Update Article' : 'Create Article'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Modal */}
      {isDocumentModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingDocument ? 'Edit Document' : 'Upload New Document'}
              </h2>
              <button
                onClick={closeDocumentModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Label *
                </label>
                <input
                  type="text"
                  value={documentForm.label}
                  onChange={(e) => setDocumentForm({ ...documentForm, label: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter document label"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={documentForm.description}
                  onChange={(e) => setDocumentForm({ ...documentForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter document description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  File URL *
                </label>
                <input
                  type="text"
                  value={documentForm.fileUrl}
                  onChange={(e) => setDocumentForm({ ...documentForm, fileUrl: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://example.com/document.pdf"
                  required
                />
                <p className="text-sm text-gray-500 mt-1">
                  Real file upload feature coming soon - for now, enter URL to existing file
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <div className="flex gap-2">
                  <select
                    value={documentForm.category}
                    onChange={(e) => setDocumentForm({ ...documentForm, category: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select or type new category</option>
                    {getUniqueCategories().map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={documentForm.category}
                    onChange={(e) => setDocumentForm({ ...documentForm, category: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Or type new category"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Keywords
                </label>
                <input
                  type="text"
                  value={documentForm.keywords}
                  onChange={(e) => setDocumentForm({ ...documentForm, keywords: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter keywords separated by commas"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Separate keywords with commas (e.g., manual, guide, reference)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={documentForm.notes}
                  onChange={(e) => setDocumentForm({ ...documentForm, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Internal notes about this document"
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={closeDocumentModal}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={editingDocument ? handleUpdateDocument : handleCreateDocument}
                disabled={saving || !documentForm.label || !documentForm.fileUrl}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                <span>{saving ? 'Saving...' : editingDocument ? 'Update Document' : 'Upload Document'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeRepositoryPage;
